import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ImageSquare } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import { PROJECTS, PROJECT_CATEGORIES } from "@/lib/siteData";

export default function Projects() {
  const [active, setActive] = useState("All");
  const filtered =
    active === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === active);

  return (
    <div data-testid="projects-page">
      <PageHeader
        eyebrow="— Projects / Gallery"
        title="A look at some of the work we've put our name on."
        intro="A snapshot of recent projects across the Waikato, Auckland and Hauraki. Filter by service to see specific examples — more being added all the time."
      />

      <section className="section-pad bg-[#F6F5F2]">
        <div className="container-fz">
          {/* Filter chips */}
          <div className="flex flex-wrap gap-2 mb-12">
            {PROJECT_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                data-testid={`projects-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] border transition-all ${
                  active === cat
                    ? "bg-[#1A1A18] text-[#F6F5F2] border-[#1A1A18]"
                    : "bg-transparent text-stone-700 border-[#D2D0C9] hover:border-stone-900 hover:text-stone-900"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Tetris-style grid */}
          <div className="grid grid-cols-1 md:grid-cols-6 auto-rows-[minmax(200px,auto)] gap-px bg-[#D2D0C9] border border-[#D2D0C9]">
            {filtered.map((p, i) => {
              const span = i % 5 === 0 ? "md:col-span-4" : i % 5 === 1 ? "md:col-span-2" : i % 5 === 2 ? "md:col-span-3" : i % 5 === 3 ? "md:col-span-3" : "md:col-span-6";
              return (
                <motion.article
                  key={`${p.title}-${i}`}
                  layout
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.03 }}
                  data-testid={`project-card-${i}`}
                  className={`${span} bg-[#F6F5F2] overflow-hidden flex flex-col transition-colors min-h-[260px] group`}
                >
                  {p.image && (
                    <div className="relative w-full h-56 md:h-64 overflow-hidden border-b border-[#D2D0C9]">
                      <img
                        src={p.image}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                      />
                      <span className="absolute top-3 right-3 text-[10px] tracking-[0.2em] uppercase text-stone-900 bg-[#F6F5F2]/95 px-2 py-1">
                        {p.category}
                      </span>
                    </div>
                  )}
                  <div className="p-8 md:p-10 flex flex-col flex-1">
                    <div className="flex items-center justify-between">
                      <span className="num-marker">0{i + 1}</span>
                      {!p.image && (
                        <span className="text-[11px] tracking-[0.2em] uppercase text-stone-500 border border-[#D2D0C9] px-2 py-1">
                          {p.category}
                        </span>
                      )}
                    </div>
                    <div className="mt-6 flex-1">
                      <h3 className="font-display text-2xl md:text-3xl text-stone-900 leading-tight">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-sm uppercase tracking-[0.18em] text-stone-500">
                        {p.location}
                      </p>
                      <p className="mt-5 text-stone-600 leading-relaxed">
                        {p.description}
                      </p>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>

          {/* Empty state */}
          {filtered.length === 0 && (
            <div className="mt-12 border border-dashed border-[#D2D0C9] p-12 text-center">
              <ImageSquare size={32} className="text-stone-400 mx-auto" />
              <p className="mt-4 text-stone-600">
                No projects under this category yet — check back soon.
              </p>
            </div>
          )}

          <div className="mt-16 flex flex-col md:flex-row gap-4 md:items-center md:justify-between border-t border-[#D2D0C9] pt-12">
            <p className="text-stone-600 max-w-xl">
              More project photos being added all the time. Want to see specific
              examples for your job? Flick us a message.
            </p>
            <Link to="/contact" data-testid="projects-contact-link" className="btn-primary">
              Get in touch <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
